/**
 * PasswordInput
 *
 * The standard Input with a show/hide toggle at its right edge. The value is
 * hidden by default. The toggle is type="button", so it never submits a form.
 * All other props, including autoComplete, pass through to the input.
 */

import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input, type InputProps } from "./input";
import { passwordToggleLabel } from "./password-input-state";

export type PasswordInputProps = Omit<InputProps, "type">;

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? "text" : "password"}
          disabled={disabled}
          className={cn("pr-9", className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((value) => !value)}
          disabled={disabled}
          aria-label={passwordToggleLabel(visible)}
          aria-pressed={visible}
          title={passwordToggleLabel(visible)}
          className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-r-md text-surface-500 transition-colors hover:text-surface-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-surface-400 dark:hover:text-surface-100"
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
